import { computed, watch, WritableComputedRef } from "vue";
import { useExplorerStore } from "@/modules/explorer/store";

export function effectiveStateInner<T, K extends keyof T>(
	getState: () => T,
	getDerivedState: () => T,
	getId: () => unknown,
	key: K
): WritableComputedRef<T[K]> {
	let hasChanged = false;

	watch(
		() => getId(),
		() => {
			hasChanged = false;
		}
	);

	return computed({
		get() {
			// First retrieve value from both state and derived state. This ensures Vue adds reactive listeners on both objects.
			const state = getState();
			const derivedState = getDerivedState();

			const originalValue = state[key];
			const derivedValue = derivedState ? derivedState[key] : null;

			// If the object has changed or there is no derived state, return the original state object value.
			if (hasChanged || !derivedState) {
				return originalValue;
			}

			// No changes have been made, show the derived document.
			return derivedValue;
		},
		set(value: T[K]) {
			hasChanged = true;
			getState()[key] = value;
		},
	});
}

export function effectiveState<T, K extends keyof T>(
	getState: () => T,
	getDerivedState: () => T,
	key: K
): WritableComputedRef<T[K]> {
	return effectiveStateInner(
		getState,
		getDerivedState,
		() => useExplorerStore().document.uuid,
		key
	);
}
